// ./src/components/features/CreateEventWizard/steps/SignupsStep.tsx
//
// "What can guests bring or help with?" — shown when the Sign-up list feature is on (ROADMAP A2b #62: a feature the
// host picks gets a step where they add what it needs; owner, 2026-09-29).
//
// #140 — chosen as the GIFT list on the Gifts step, it says so: the gifts guests choose from, starting from the type's
// gift ideas ("Diapers", "Books") rather than its sign-up ideas ("Snacks").
//
// Items are real rows on the event, made in the Sign-up list page's own `SignupItemEditDrawer` under a provider scoped
// by the event (the drawer resolves create/update through it). The list's failure is the READ's own result, never the
// context's `lastError`, which a failed create sets too.

import React, { useCallback, useEffect, useState } from 'react';
import { Stack, Text } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';

import { FEATURE_CODES } from '@/config';
import { SignupItemsProvider, useSignupItemsContext } from '@/contexts';
import { useCreateGate, useToast } from '@/hooks';
import { SignupItem, WHOLE_SET } from '@/utils/api';
import { ConfirmModal } from '@/components/generic';
import { SignupItemEditDrawer } from '@/components/features/SignupItems';
import { refusalReason } from '@/utils';

import { useEventWizard } from '../WizardContext';
import { WizardItemRow } from '../components/WizardItemRow';
import { WizardListSection } from '../components/WizardListSection';
import { startersFor, unusedStarters } from '../wizardStarters';
import { useTypeGiftIdeas, useTypeIdeas } from '../useTypeIdeas';
import { isGiftList } from '../giftRegistry';

const SignupsStepBody: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const { draft, saving } = useEventWizard();
  const giftList = isGiftList(draft);
  const { signupItems, list, isLoaded, remove } = useSignupItemsContext();
  const gate = useCreateGate(FEATURE_CODES.ENGAGEMENT, { eventId });

  const [readError, setReadError] = useState<OpResult | null>(null);
  const [drawer, setDrawer] = useState<{ recordId: string | null; name?: string } | null>(null);
  const [pendingRemove, setPendingRemove] = useState<SignupItem | null>(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    const res = await list({ eventId: [eventId] }, { page: 1, pageSize: WHOLE_SET }, undefined, true);

    setReadError(res.didSucceed() ? null : res);
  }, [list, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  // ⓘ The TYPE's own ideas (#62 P6(h)) — a potluck's "Dessert", a book club's "Which book next?". One tap opens the
  //   real drawer with the name filled in; nothing is made until the host saves. Hidden once a row has that name.
  //   ⓘ No generic set here: a sign-up item or a poll means nothing without the type behind it.
  const typeIdeas = useTypeIdeas('signup_list');
  const giftIdeas = useTypeGiftIdeas();
  const starters = unusedStarters(startersFor(giftList ? giftIdeas : typeIdeas, [], giftList ? 'Gift' : 'ListCheck'), signupItems.map((row) => row.name));

  const afterSave = () => {
    setDrawer(null);
    void load();
  };

  const confirmRemove = async () => {
    if (!pendingRemove?.id) {
      return;
    }

    setRemoving(true);

    const res = await remove(pendingRemove.id);

    setRemoving(false);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPendingRemove(null);
    void load();
  };

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {giftList
          ? t('events:wizard.signups.giftIntro', {
              defaultValue:
                'List the gifts guests can choose from. Each guest picks one on the event page, so nothing is bought twice — and you see who chose what.',
            })
          : t('events:wizard.signups.intro', {
              defaultValue:
                'List what guests can sign up for. They choose on the event page, and you see who signed up for what.',
            })}
      </Text>

      <WizardListSection
        title={giftList ? t('events:wizard.signups.giftTitle', { defaultValue: 'Gifts' }) : t('events:wizard.signups.title', { defaultValue: 'Sign-up items' })}
        addLabel={giftList ? t('events:wizard.signups.giftAdd', { defaultValue: 'Add a gift' }) : t('events:wizard.signups.add', { defaultValue: 'Add an item' })}
        onAdd={() => gate.canCreate && setDrawer({ recordId: null })}
        starters={starters.map((s) => ({ key: s.key, label: s.label, icon: s.icon, onClick: () => gate.canCreate && setDrawer({ recordId: null, name: s.label }) }))}
        gate={gate}
        gateWhat={t('events:wizard.signups.another', { defaultValue: 'another item' })}
        disabled={saving}
        isLoaded={isLoaded || !!readError}
        readError={readError}
        onRetry={() => void load()}
        errorTitle={t('events:wizard.signups.loadFailed', { defaultValue: 'Could not load the sign-up items' })}
        operation="signupItemList"
        emptyText={t('events:wizard.signups.none', { defaultValue: 'No items yet.' })}
        isEmpty={signupItems.length === 0}
      >
        {signupItems.map((item) => (
          <WizardItemRow
            key={item.id}
            icon="ClipboardList"
            title={item.name ?? ''}
            subtitle={
              item.quantityAvailable != null
                ? t('events:wizard.signups.quantity', { defaultValue: '{{count}} needed', count: item.quantityAvailable })
                : t('events:wizard.signups.unlimited', { defaultValue: 'As many as guests like' })
            }
            disabled={saving}
            onEdit={() => setDrawer({ recordId: item.id as string })}
            onRemove={() => setPendingRemove(item)}
          />
        ))}
      </WizardListSection>

      <SignupItemEditDrawer
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        recordId={drawer?.recordId ?? null}
        eventId={eventId}
        defaultName={drawer?.name ?? null}
        /* #140 — a gift is taken once: a new gift starts at one available (the host may raise it). */
        defaultQuantity={giftList ? 1 : null}
        onCreated={afterSave}
        onUpdated={afterSave}
      />

      <ConfirmModal
        opened={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        type="danger"
        confirmInProgress={removing}
        title={t('events:wizard.signups.removeTitle', { defaultValue: 'Remove this item?' })}
        message={t('events:wizard.tickets.removeMessage', { defaultValue: '“{{name}}” will be removed from this event.', name: pendingRemove?.name ?? '' })}
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />
    </Stack>
  );
};

export const SignupsStep: React.FC = () => {
  const { t } = useTranslation(['events']);
  const { createdEventId } = useEventWizard();

  if (!createdEventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  return (
    <SignupItemsProvider initialFilters={{ eventId: [createdEventId] }} pageSize={WHOLE_SET} autoLoad={false}>
      <SignupsStepBody eventId={createdEventId} />
    </SignupItemsProvider>
  );
};
